import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { capitalizeName, lowerEmail } from "@/lib/text";

const PROFILES = [
  "Futur(e) marié(e)",
  "Organisateur d'événement",
  "Client déjà réservé",
  "Curieux(se) / entourage",
  "Testeur tech",
  "Autre",
];

const DEVICES = [
  "iPhone / iPad",
  "Android",
  "Ordinateur Mac",
  "Ordinateur Windows",
  "Autre",
];

export default function BetaFeedback() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    profile: "",
    rating: 0,
    what_worked: "",
    what_didnt: "",
    suggestions: "",
    device: "",
    would_recommend: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Ton prénom stp");
    if (form.rating < 1) return toast.error("Donne une note (1 à 10)");
    setSubmitting(true);
    try {
      await api.post("/beta-feedback", {
        ...form,
        name: form.name.trim(),
        email: form.email.trim() || null,
      });
      setDone(true);
    } catch (err) {
      toast.error("Erreur lors de l'envoi. Réessaie ?");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-xl text-center"
          data-testid="beta-feedback-success"
        >
          <div className="text-6xl mb-6">🙏</div>
          <h1 className="font-serif italic text-4xl text-[#dcb457] mb-4">Merci mille fois</h1>
          <p className="text-white/70 text-lg leading-relaxed">
            Ton retour est précieux — il va nous aider à peaufiner le site avant sa mise en ligne
            définitive. Yannick te remercie personnellement 🎧
          </p>
          <a
            href="/"
            className="inline-block mt-10 px-8 py-3 border border-[#dcb457] text-[#dcb457] uppercase tracking-[0.2em] text-xs hover:bg-[#dcb457] hover:text-black transition"
            data-testid="beta-feedback-back-home"
          >
            Retour au site
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white px-6 py-16 lg:py-24">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#dcb457] mb-3">
            Programme bêta · My Animation
          </div>
          <h1 className="font-serif italic text-4xl lg:text-5xl text-white mb-4">
            Ton avis <span className="text-[#dcb457]">compte double.</span>
          </h1>
          <p className="text-white/60 leading-relaxed text-base mb-2">
            Merci d'avoir accepté de tester le nouveau site My Animation avant sa mise en ligne
            définitive. Ton feedback nous aide à repérer les derniers détails à corriger.
          </p>
          <p className="text-white/40 text-sm">
            5 à 10 minutes tout au plus. Aucun champ obligatoire à part ton prénom et la note.
          </p>
        </motion.div>

        <div className="my-10 h-px bg-gradient-to-r from-transparent via-[#dcb457]/40 to-transparent" />

        <form onSubmit={submit} className="space-y-8" data-testid="beta-feedback-form">
          {/* Identité */}
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Ton prénom *">
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                onBlur={(e) => set("name", capitalizeName(e.target.value))}
                required
                className="input-luxe"
                placeholder="Sarah"
                data-testid="feedback-input-name"
              />
            </Field>
            <Field label="Email (optionnel — si tu veux qu'on te réponde)">
              <input
                type="email"
                value={form.email}
                onChange={(e) => set("email", lowerEmail(e.target.value))}
                className="input-luxe"
                placeholder="sarah@exemple.fr"
                data-testid="feedback-input-email"
              />
            </Field>
          </div>

          {/* Profil */}
          <Field label="Tu es plutôt…">
            <div className="flex flex-wrap gap-2" data-testid="feedback-profile-group">
              {PROFILES.map((p) => (
                <ChipButton
                  key={p}
                  active={form.profile === p}
                  onClick={() => set("profile", p)}
                  testid={`feedback-profile-${p.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                >
                  {p}
                </ChipButton>
              ))}
            </div>
          </Field>

          {/* Rating */}
          <Field label="Note globale du site *">
            <div className="flex flex-wrap gap-2" data-testid="feedback-rating-group">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => set("rating", n)}
                  className={`w-11 h-11 rounded-full border-2 text-sm font-semibold transition ${
                    form.rating >= n
                      ? "bg-[#dcb457] border-[#dcb457] text-black"
                      : "border-white/20 text-white/50 hover:border-[#dcb457] hover:text-[#dcb457]"
                  }`}
                  data-testid={`feedback-rating-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="mt-2 text-xs text-white/40">
              {form.rating === 0
                ? "Choisis une note de 1 à 10"
                : form.rating <= 5
                ? "😐 Il y a du travail — dis-nous quoi améliorer plus bas."
                : form.rating <= 7
                ? "🙂 Pas mal — quels détails te dérangent ?"
                : form.rating <= 9
                ? "😃 Très bien — un dernier peaufinage ?"
                : "🤩 Parfait ! Merci !"}
            </div>
          </Field>

          {/* Textareas */}
          <Field label="Ce qui a bien fonctionné pour toi">
            <textarea
              value={form.what_worked}
              onChange={(e) => set("what_worked", e.target.value)}
              className="input-luxe min-h-[90px]"
              placeholder="Ex : la prise de rendez-vous, le design, la lecture des tarifs…"
              data-testid="feedback-what-worked"
            />
          </Field>

          <Field label="Ce qui n'a pas fonctionné / bugs rencontrés">
            <textarea
              value={form.what_didnt}
              onChange={(e) => set("what_didnt", e.target.value)}
              className="input-luxe min-h-[90px]"
              placeholder="Ex : bouton qui ne marche pas, page lente, texte flou…"
              data-testid="feedback-what-didnt"
            />
          </Field>

          <Field label="Suggestions d'amélioration">
            <textarea
              value={form.suggestions}
              onChange={(e) => set("suggestions", e.target.value)}
              className="input-luxe min-h-[90px]"
              placeholder="Une fonctionnalité qui manque ? Une idée pour clarifier une page ?"
              data-testid="feedback-suggestions"
            />
          </Field>

          {/* Device */}
          <Field label="Depuis quel appareil as-tu testé ?">
            <div className="flex flex-wrap gap-2" data-testid="feedback-device-group">
              {DEVICES.map((d) => (
                <ChipButton
                  key={d}
                  active={form.device === d}
                  onClick={() => set("device", d)}
                  testid={`feedback-device-${d.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                >
                  {d}
                </ChipButton>
              ))}
            </div>
          </Field>

          {/* Recommend */}
          <Field label="Tu recommanderais My Animation à un(e) ami(e) ?">
            <div className="flex gap-3" data-testid="feedback-recommend-group">
              <ChipButton
                active={form.would_recommend === true}
                onClick={() => set("would_recommend", true)}
                testid="feedback-recommend-yes"
              >
                👍 Oui
              </ChipButton>
              <ChipButton
                active={form.would_recommend === false}
                onClick={() => set("would_recommend", false)}
                testid="feedback-recommend-no"
              >
                👎 Pas encore
              </ChipButton>
            </div>
          </Field>

          {/* Submit */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={submitting}
              className="w-full md:w-auto px-10 py-4 bg-[#dcb457] text-black uppercase tracking-[0.25em] text-xs font-semibold hover:bg-[#f0d888] transition disabled:opacity-50"
              data-testid="feedback-submit"
            >
              {submitting ? "Envoi en cours…" : "Envoyer mon retour"}
            </button>
            <p className="text-xs text-white/40 mt-4">
              Ton feedback est envoyé directement à Yannick — aucune donnée n'est partagée avec des
              tiers.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-white/60 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function ChipButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full border text-sm transition ${
        active
          ? "bg-[#dcb457] border-[#dcb457] text-black"
          : "border-white/20 text-white/60 hover:border-[#dcb457] hover:text-[#dcb457]"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
